import { Fragment, useState, useMemo } from "react";
import {
  Loading,
  getCurrentMonth,
  ClinicFilterBar,
  SummaryCard,
  Button,
  showSuccess,
  showApiError,
  usePersistedState,
} from "@common";
import {
  useSchedulePayments,
  useCreateSchedulePayment,
  useUpdateSchedulePayment,
  useDeleteSchedulePayment,
  useDeleteSchedulePaymentException,
  useBankAccounts,
} from "@api/hooks";
import { useAuth } from "@api/useAuth";
import { canManageSchedulePayments, canDeleteSchedulePayments } from "@utils";
import useClinicFilterOptions from "@hooks/useClinicFilterOptions";
import CurrencyBadge from "../CurrencyBadge";
import PaymentStatusBadge from "../PaymentStatusBadge";
import SchedulePaymentModal from "../SchedulePaymentModal";
import { occurrencesInRange } from "../scheduleOccurrences";

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmt(v) {
  if (v == null) return "—";
  return `$${Math.round(Math.abs(v)).toLocaleString("en-CA")}`;
}

function fmtDate(d) {
  if (!d) return "—";
  return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

// An ISO date string, formatted like the rest of the table. Parsed field by
// field rather than through `new Date(s)`, which reads a bare YYYY-MM-DD as UTC
// and lands on the previous day for anyone west of Greenwich.
function fmtISODate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// How far ahead to look for the next occurrence. Comfortably clears an annual
// schedule's gap, so every interval resolves in one pass.
const NEXT_DUE_HORIZON_DAYS = 800;

function toISODate(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * The next upcoming occurrence of `schedule`, or null when it has none left.
 *
 * Delegates to `occurrencesInRange` rather than re-deriving the recurrence rules.
 * This function used to carry its own copy of them, which meant it knew nothing
 * about the weekday split or the holiday move: a Tue/Thu schedule reported its
 * start date's weekday, and a payment moved off Canada Day still showed Jul 1.
 * Three copies of the rules was already one too many.
 *
 * @param {object} schedule serialized SchedulePayment (needs `exceptions`)
 */
function computeNextDue(schedule, today = new Date()) {
  if (!schedule?.start_date) return null;
  const from = new Date(today);
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + NEXT_DUE_HORIZON_DAYS);

  const [next] = occurrencesInRange(schedule, toISODate(from), toISODate(to));
  if (!next) return null;
  const [y, m, d] = next.date.split("-").map(Number);
  return new Date(y, m - 1, d);
}

// ─── table ────────────────────────────────────────────────────────────────────

function Chevron({ open, className = "w-3.5 h-3.5" }) {
  return (
    <svg
      className={`${className} transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
      viewBox="0 0 10 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const TABLE_COLS = [
  { key: "payment", label: "Payment" },
  // A payment is drawn from a specific account now, and a clinic can own more
  // than one — so the account has to be visible per row, not just per group.
  { key: "account", label: "Account" },
  { key: "schedule", label: "Schedule" },
  { key: "nextDue", label: "Next Due" },
  { key: "status", label: "Status" },
  { key: "createdBy", label: "Created By" },
  { key: "amount", label: "Amount" },
];

/**
 * The payment rows for one group.
 *
 * `onRestore` un-skips one occurrence, by deleting the exception row that
 * carries the skip — that row IS the record of the decision, so removing it
 * puts the occurrence back exactly where the recurring rule always said it
 * went. Withheld from a read-only viewer, like `onEdit`.
 *
 * This is the only place a skipped occurrence is visible at all: the cash-flow
 * grid drops them by design, so without this list a skip would be a one-way
 * door.
 */
function PaymentTable({ payments, onEdit, onRestore, restoring }) {
  // Which rows have their skipped-occurrence list open. Local to the table —
  // nothing above it needs to know, and it should not survive a collapse of the
  // enclosing group.
  const [openSkips, setOpenSkips] = useState(() => new Set());
  const toggleSkips = (id) =>
    setOpenSkips((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <table className="cf-table w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
          {TABLE_COLS.map((col) => (
            <th
              key={col.key}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary whitespace-nowrap ${
                col.key === "amount" ? "text-right" : "text-left"
              }`}
            >
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {payments.map((p, i) => {
          const nextDue = computeNextDue(p.raw);
          const skipsOpen = openSkips.has(p.id);
          return (
            <Fragment key={p.id ?? i}>
              <tr
                onClick={onEdit ? () => onEdit(p) : undefined}
                className={`border-b border-palette-gray-divider last:border-0 transition-colors ${
                  onEdit ? "cursor-pointer" : ""
                }`}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Chevron
                      open={false}
                      className="w-2.5 h-2.5 text-palette-gray-placeholder shrink-0"
                    />
                    {/* `notes` is an unbounded TextField, and this table lays out
                      auto-width — so without a cap the column grew to the
                      longest note on screen and squeezed every other column out
                      of shape. `truncate` alone could not do it: it needs a
                      bounded width to ellipsize against. Capped and given a
                      `title`, the same way the Account and Created By cells
                      handle their own long values, so the full text is still one
                      hover away (and the row opens the editor on click). */}
                    <div className="min-w-0 max-w-[280px]">
                      {/* `min-w-0` so the span can shrink below its content and
                        the `truncate` on it actually fires: a flex item defaults
                        to min-width:auto, and `expense_type` takes free text
                        (up to 64 chars) when "Other" is picked. */}
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="truncate font-semibold text-palette-gray-primary"
                          title={p.category ?? undefined}
                        >
                          {p.category}
                        </span>
                      </div>
                      <div
                        className="truncate text-xs text-palette-gray-secondary"
                        title={p.notes || p.payee || undefined}
                      >
                        {p.notes || p.payee || "—"}
                      </div>
                      {/* stopPropagation: the whole row opens the schedule editor,
                        and expanding the skip list is a different intent. */}
                      {p.skipped.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSkips(p.id);
                          }}
                          className="mt-1 inline-flex items-center gap-1 rounded-full border border-palette-amber-border bg-palette-amber-soft px-2 py-0.5 text-[11px] font-medium text-palette-amber-text transition-colors hover:bg-palette-amber-muted"
                        >
                          <Chevron open={skipsOpen} className="h-2 w-2" />
                          {p.skipped.length} skipped
                        </button>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="truncate text-palette-gray-heading max-w-[200px]"
                      title={p.accountLabel ?? undefined}
                    >
                      {p.accountLabel ?? "—"}
                    </span>
                    {p.accountCurrency && <CurrencyBadge currency={p.accountCurrency} />}
                  </div>
                </td>
                <td className="px-4 py-3 text-palette-gray-heading whitespace-nowrap">
                  {p.intervalLabel}
                </td>
                <td className="px-4 py-3 text-palette-gray-heading whitespace-nowrap">
                  {fmtDate(nextDue)}
                </td>
                <td className="px-4 py-3">
                  <PaymentStatusBadge status={p.status} />
                </td>
                <td className="px-4 py-3 text-palette-gray-heading">
                  <span
                    className="block truncate max-w-[160px]"
                    title={p.createdByEmail ?? undefined}
                  >
                    {p.createdBy ?? "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums text-palette-gray-primary whitespace-nowrap">
                  {fmt(p.amount)}
                </td>
              </tr>
              {skipsOpen && (
                <tr className="border-b border-palette-gray-divider bg-palette-gray-subtle">
                  <td colSpan={TABLE_COLS.length} className="px-4 py-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-palette-gray-secondary">
                      Skipped occurrences
                    </div>
                    <div className="space-y-1.5">
                      {p.skipped.map((exc) => (
                        <div
                          key={exc.id}
                          className="flex items-center justify-between gap-4 rounded-lg bg-white px-3 py-2"
                        >
                          <div className="min-w-0">
                            <span className="font-medium text-palette-gray-primary">
                              {fmtISODate(exc.original_date)}
                            </span>
                            <span className="ml-2 text-xs text-palette-gray-secondary">
                              {exc.notes || "No reason given"}
                              {exc.created_by_name || exc.created_by_email
                                ? ` — ${exc.created_by_name || exc.created_by_email}`
                                : ""}
                            </span>
                          </div>
                          {onRestore && (
                            <button
                              type="button"
                              disabled={restoring}
                              onClick={(e) => {
                                e.stopPropagation();
                                onRestore(exc);
                              }}
                              className="shrink-0 rounded-lg border border-palette-gray-divider px-2.5 py-1 text-xs font-medium text-palette-gray-heading transition-colors hover:bg-palette-gray-subtle disabled:opacity-50"
                            >
                              Restore
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function ClinicGroup({ group, expanded, onToggle, onEdit, onRestore, restoring }) {
  return (
    <div className="rounded-2xl border border-palette-gray-divider bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-palette-gray-subtle transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Chevron open={expanded} />
          <div className="min-w-0">
            <div className="font-semibold text-palette-gray-primary truncate">{group.name}</div>
            <div className="text-sm text-palette-gray-secondary truncate">{group.subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-6 shrink-0">
          <span className="text-sm text-palette-gray-secondary whitespace-nowrap">
            {group.rows.length} payment{group.rows.length === 1 ? "" : "s"}
          </span>
          <span className="w-28 text-right text-base font-bold tabular-nums text-palette-gray-primary">
            {fmt(group.total)}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto border-t border-palette-gray-divider">
          <PaymentTable
            payments={group.rows}
            onEdit={onEdit}
            onRestore={onRestore}
            restoring={restoring}
          />
        </div>
      )}
    </div>
  );
}

// ─── empty state ──────────────────────────────────────────────────────────────

// `awaitingSelection` is the pre-request state — no clinic picked, so no query
// ran. Worded apart from a genuinely empty result, and without the "add one"
// prompt, which would be the wrong next step.
function EmptyState({ canEdit, awaitingSelection, noun = "clinics" }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-palette-gray-divider bg-white py-20 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-palette-gray-subtle">
        <svg
          className="h-6 w-6 text-palette-gray-placeholder"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5"
          />
        </svg>
      </div>
      <p className="text-sm font-semibold text-palette-gray-heading">
        {awaitingSelection ? `Select one or more ${noun}` : "No scheduled payments"}
      </p>
      {awaitingSelection ? (
        <p className="mt-1 text-sm text-palette-gray-placeholder">
          Pick {noun === "entities" ? "an entity" : "a clinic"} above to see its scheduled payments.
        </p>
      ) : (
        canEdit && (
          <p className="mt-1 text-sm text-palette-gray-placeholder">
            Add a payment to get started.
          </p>
        )
      )}
    </div>
  );
}

// ─── main ─────────────────────────────────────────────────────────────────────

/**
 * "All Payments" tab: the full schedule list, grouped and editable.
 *
 * Two scopes, chosen by whether `entityIds` is passed:
 *
 *   CLINIC MODE (default, AccessClinicCashFlow) — only accounts that belong to a
 *     clinic are schedulable (`has_clinic=true`), payments group by clinic.
 *   ENTITY MODE (DensNDenteCashFlow, `entityIds` given) — only ENTITY-level
 *     accounts (`has_clinic=false`), payments group by entity.
 *
 * The two account sets are disjoint by construction, so neither page can
 * schedule against the other's accounts.
 */
export default function AllPayments({
  canEdit: canEditProp,
  // Entity mode: the entity PKs to scope accounts to. `null` = clinic mode.
  entityIds = null,
  // Entity mode only: the host page owns the entity selection and passes the
  // picker through, so Month + Entity render on one filter bar.
  entityOptions = [],
  entityValue = [],
  onEntityChange = null,
}) {
  const entityMode = Array.isArray(entityIds);
  const { user } = useAuth();
  // Write access mirrors the backend `ClinicBasedCRUDPermission` on
  // apps/schedule_payment — Bookkeeper reaches this tab but is read-only, so
  // the add/edit affordances must be hidden or it fills in a modal and eats a
  // 403 on submit. A host may pass `canEdit={false}` to embed this read-only.
  const canEdit = canEditProp ?? canManageSchedulePayments(user);
  // Narrower than `canEdit` on purpose — an area manager or practitioner lead
  // can edit a payment but the backend refuses them the DELETE.
  const canDelete = canEdit && canDeleteSchedulePayments(user);

  // Persist the filter selection so it survives a refresh / tab switch.
  const [months, setMonths] = usePersistedState("schedulePayment:months", () => [
    getCurrentMonth(),
  ]);
  const [areaManagerFilter, setAreaManagerFilter] = usePersistedState(
    "schedulePayment:areaManagerFilter",
    "",
  );
  const [clinicFilters, setClinicFilters] = usePersistedState("schedulePayment:clinicFilters", []);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { clinicsQuery, areaManagerOptions, clinicOptions } =
    useClinicFilterOptions(areaManagerFilter);

  // Accounts a payment can be drawn from. Always `status=active` — an inactive
  // account should not be selectable for a new schedule. `has_clinic` splits the
  // two scopes: clinic-owned accounts here, entity-level ones on the Dens N
  // Dente page, with no overlap either way. Labels come from the local registry
  // (resolved once by `manage.py backfill_bank_account_labels`), no NetSuite call.
  const bankAccountsQuery = useBankAccounts(
    entityMode
      ? {
          status: "active",
          has_clinic: "false",
          entity_id: (entityIds ?? []).join(","),
          limit: 500,
        }
      : { status: "active", has_clinic: "true", limit: 500 },
    { enabled: !entityMode || (entityIds ?? []).length > 0 },
  );
  const accounts = useMemo(
    () => bankAccountsQuery.data?.results ?? bankAccountsQuery.data ?? [],
    [bankAccountsQuery.data],
  );
  const accountOptions = useMemo(
    () =>
      accounts.map((a) => ({
        value: a.id,
        label: a.label,
        clinicCode: a.clinic_code ?? null,
        entityName: a.entity_name ?? null,
        currency: a.currency,
      })),
    [accounts],
  );

  // Filter by clinic_id (the FK column) rather than code. This also fixes a real
  // bug: the old version only sent a filter when EXACTLY ONE clinic was selected
  // (the backend took a single code), and nothing filtered client-side — so
  // picking two clinics silently showed every clinic's payments. `clinic_id`
  // takes a CSV list, so all selections are honoured.
  const selectedClinicIds = useMemo(() => {
    if (clinicFilters.length === 0) return [];
    const idByCode = new Map(clinicOptions.map((o) => [o.value, o.id]));
    return clinicFilters.map((code) => idByCode.get(code)).filter((id) => id != null);
  }, [clinicFilters, clinicOptions]);

  const listFilters = useMemo(() => {
    if (entityMode) {
      // Scope by the entity's own account ids. There is no entity param on this
      // endpoint — and going through one would be wrong anyway, since a payment
      // is tied to an account, not an entity.
      return { bank_account: accountOptions.map((o) => o.value).join(",") };
    }
    return selectedClinicIds.length > 0 ? { clinic_id: selectedClinicIds.join(",") } : {};
  }, [entityMode, accountOptions, selectedClinicIds]);

  const paymentsQuery = useSchedulePayments(listFilters, {
    // An unfiltered request returns EVERY clinic's payments, so neither mode may
    // fire before it has something to scope by: entity mode waits for the
    // account list, clinic mode for a clinic to be picked.
    enabled: entityMode ? accountOptions.length > 0 : selectedClinicIds.length > 0,
  });
  const createPayment = useCreateSchedulePayment();
  const updatePayment = useUpdateSchedulePayment();
  const deletePayment = useDeleteSchedulePayment();
  const deleteException = useDeleteSchedulePaymentException();

  const payments = useMemo(() => {
    const rows = paymentsQuery.data?.results ?? paymentsQuery.data ?? [];
    return rows.map((p) => ({
      id: p.id,
      // `clinic` and `entity_name` are both served read-only, resolved through
      // the account, so grouping works in either mode after the FK move.
      clinic: p.clinic,
      entityName: p.entity_name,
      accountId: p.bank_account,
      accountLabel: p.bank_account_label,
      accountCurrency: p.bank_account_currency,
      category: p.expense_type_display,
      payee: p.payment_type_display,
      amount: p.amount != null ? Number(p.amount) : null,
      dueDate: p.start_date,
      endDate: p.end_date,
      interval: p.payment_interval,
      intervalLabel: p.payment_interval_display,
      status: p.status,
      notes: p.notes,
      // Null on rows created before created_by existed, and on the 2026-07-13
      // bulk import — hence the fallback rather than an assumed author.
      createdBy: p.created_by_name || p.created_by_email || null,
      createdByEmail: p.created_by_email || null,
      // Occurrences somebody removed from the cash-flow grid. A null `new_date`
      // IS the skip (see SchedulePaymentException) — the same rows carrying a
      // date are moves, and belong to the grid rather than to this list.
      //
      // Read straight off the nested `exceptions` the schedule serializer
      // already returns, so the list costs no extra request.
      skipped: (p.exceptions ?? [])
        .filter((e) => !e.new_date)
        .sort((a, b) => (a.original_date < b.original_date ? 1 : -1)),
      raw: p,
    }));
  }, [paymentsQuery.data]);

  const [expandedClinics, setExpandedClinics] = useState(() => new Set());

  function toggleClinic(code) {
    setExpandedClinics((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  }

  // Grouped by entity in entity mode, by clinic otherwise — the group key is
  // whichever of the two the account actually hangs off.
  const clinicGroups = useMemo(() => {
    const clinicMap = new Map(clinicOptions.map((c) => [c.value, c]));
    const byKey = new Map();
    for (const p of payments) {
      const key = (entityMode ? p.entityName : p.clinic) || "";
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(p);
    }
    return Array.from(byKey.entries())
      .map(([code, rows]) => {
        const info = entityMode ? null : clinicMap.get(code);
        return {
          code,
          name: entityMode ? code || "Unknown entity" : info?.name || code || "Unknown clinic",
          subtitle: entityMode
            ? `${rows.length} scheduled payment${rows.length === 1 ? "" : "s"}`
            : info?.subtitle || code || "—",
          rows,
          total: rows.reduce((s, p) => s + (p.amount ?? 0), 0),
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [payments, clinicOptions, entityMode]);

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row.raw);
    setModalOpen(true);
  }

  const initialValues = useMemo(() => {
    if (!editing) return null;
    return {
      bank_account: editing.bank_account ?? "",
      expense_type: editing.expense_type,
      payment_type: editing.payment_type,
      payment_interval: editing.payment_interval,
      // Weekly/biweekly weekday split. Defaulted to [] so an older row that
      // predates the column reopens as "no split" instead of undefined.
      payment_weekdays: editing.payment_weekdays ?? [],
      amount: editing.amount ?? "",
      start_date: editing.start_date ?? "",
      end_date: editing.end_date ?? "",
      notes: editing.notes ?? "",
      status: editing.status,
    };
  }, [editing]);

  async function handleSave(form) {
    const payload = {
      bank_account: form.bank_account || undefined,
      expense_type: form.expense_type,
      payment_type: form.payment_type,
      payment_interval: form.payment_interval,
      payment_weekdays: form.payment_weekdays ?? [],
      amount: form.amount,
      start_date: form.start_date,
      end_date: form.end_date || null,
      notes: form.notes,
      status: form.status,
    };
    try {
      if (editing) {
        await updatePayment.mutateAsync({ id: editing.id, ...payload });
        showSuccess("Scheduled payment updated.");
      } else {
        await createPayment.mutateAsync(payload);
        showSuccess("Scheduled payment created.");
      }
      setModalOpen(false);
      setEditing(null);
    } catch (err) {
      showApiError(err);
    }
  }

  // Un-skip one occurrence. The exception row carries the skip and nothing
  // else, so deleting it hands the occurrence back to the recurring rule —
  // which puts it on the date it always had, at the schedule's own amount.
  async function handleRestore(exc) {
    try {
      await deleteException.mutateAsync(exc.id);
      showSuccess(`Payment restored to ${fmtISODate(exc.original_date)}.`);
    } catch (err) {
      showApiError(err);
    }
  }

  async function handleDelete() {
    if (!editing) return;
    try {
      await deletePayment.mutateAsync(editing.id);
      showSuccess("Scheduled payment deleted.");
      setModalOpen(false);
      setEditing(null);
    } catch (err) {
      showApiError(err);
    }
  }

  const totals = useMemo(
    () => ({
      total: payments.reduce((s, p) => s + (p.amount ?? 0), 0),
      pending: payments
        .filter((p) => p.status === "pending")
        .reduce((s, p) => s + (p.amount ?? 0), 0),
      paid: payments.filter((p) => p.status === "paid").reduce((s, p) => s + (p.amount ?? 0), 0),
      overdue: payments
        .filter((p) => p.status === "overdue")
        .reduce((s, p) => s + (p.amount ?? 0), 0),
    }),
    [payments],
  );

  return (
    <>
      {/* Summary cards */}
      <div className="flex flex-wrap gap-3 mb-6">
        <SummaryCard label="Total Scheduled" value={fmt(totals.total)} />
        <SummaryCard
          label="Pending"
          value={fmt(totals.pending)}
          valueClass="text-palette-amber-badge"
        />
        <SummaryCard label="Paid" value={fmt(totals.paid)} valueClass="text-smiles-green" />
        <SummaryCard
          label="Overdue"
          value={fmt(totals.overdue)}
          valueClass="text-smiles-redOrange"
        />
      </div>

      {/* Filters + Add button */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        {entityMode ? (
          // Month + Entity. No Area Manager (an entity has none) and no clinic
          // picker — the host page owns the entity selection.
          <ClinicFilterBar
            months={months}
            onMonthsChange={setMonths}
            showAreaManager={false}
            showClinic={!!onEntityChange}
            entityOptions={entityOptions}
            entityValue={entityValue}
            onEntityChange={onEntityChange ?? undefined}
          />
        ) : (
          <ClinicFilterBar
            months={months}
            onMonthsChange={setMonths}
            areaManagerFilter={areaManagerFilter}
            areaManagerOptions={areaManagerOptions}
            onAreaManagerChange={(v) => {
              setAreaManagerFilter(v);
              setClinicFilters([]);
            }}
            clinicOptions={clinicOptions}
            clinicValue={clinicFilters}
            onClinicChange={setClinicFilters}
          />
        )}
        {canEdit && (
          <Button
            variant="primary"
            size="sm"
            className="gap-2 h-9 font-semibold shrink-0"
            onClick={openCreate}
          >
            <svg
              className="w-4 h-4 shrink-0"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M8 3v10M3 8h10" strokeLinecap="round" />
            </svg>
            Add Payment
          </Button>
        )}
      </div>

      {/* Content. In entity mode the payments query stays disabled until the
          accounts arrive, and a disabled query does not report isLoading — so
          the account query has to be part of the condition or the empty state
          flashes before any request is made. */}
      {(entityMode ? bankAccountsQuery.isLoading : clinicsQuery.isLoading) ||
      paymentsQuery.isLoading ? (
        <Loading text="Loading payments..." />
      ) : payments.length === 0 ? (
        <EmptyState
          noun={entityMode ? "entities" : "clinics"}
          canEdit={canEdit}
          awaitingSelection={
            entityMode ? (entityIds ?? []).length === 0 : selectedClinicIds.length === 0
          }
        />
      ) : (
        <div className="space-y-3">
          {clinicGroups.map((group) => (
            <ClinicGroup
              key={group.code}
              group={group}
              expanded={expandedClinics.has(group.code)}
              onToggle={() => toggleClinic(group.code)}
              onEdit={canEdit ? openEdit : undefined}
              onRestore={canEdit ? handleRestore : undefined}
              restoring={deleteException.isPending}
            />
          ))}
        </div>
      )}

      <SchedulePaymentModal
        isOpen={canEdit && modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
        saving={createPayment.isPending || updatePayment.isPending}
        accountOptions={accountOptions}
        clinicOptions={clinicOptions}
        groupBy={entityMode ? "entity" : "clinic"}
        initialValues={initialValues}
        isEdit={!!editing}
        onDelete={canDelete ? handleDelete : undefined}
        deleting={deletePayment.isPending}
      />
    </>
  );
}
